import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  FileText, MessageSquare, Phone, Mail, Clock, ChevronDown, ChevronRight,
  Search, Filter, Pencil, Trash2, RotateCcw, Download, Sheet, X, Save, Archive
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LeadDetail({ lead, onEdit, onTrash }) {
  const [open, setOpen] = useState(false);
  const lines = (lead.message || "").split("\n").filter(Boolean);
  const ts = lead.created_at ? new Date(lead.created_at).toLocaleString() : "";

  return (
    <div data-testid={`lead-row-${lead.id}`} className="border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
          <FileText className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 truncate">{lead.name}</p>
            {lead.insurance_type && <Badge variant="outline" className="text-[10px] shrink-0">{lead.insurance_type}</Badge>}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
            {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
            {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
            {ts && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ts}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          <button data-testid={`edit-lead-${lead.id}`} onClick={() => onEdit(lead)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Edit lead">
            <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
          </button>
          <button data-testid={`trash-lead-${lead.id}`} onClick={() => onTrash(lead.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Move to trash">
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
          </button>
        </div>
        {lines.length > 0 && (open ? <ChevronDown className="h-4 w-4 text-slate-300 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />)}
      </div>
      {open && lines.length > 0 && (
        <div className="px-4 pb-3 border-t border-slate-50">
          {lead.notes && (
            <div className="mt-2 bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-xs text-amber-800">
              <span className="font-semibold">Notes:</span> {lead.notes}
            </div>
          )}
          <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs text-slate-600 font-mono space-y-0.5 max-h-60 overflow-y-auto">
            {lines.map((line, i) => {
              if (line.startsWith("---")) return <p key={i} className="font-bold text-slate-800 mt-2 pt-2 border-t border-slate-200">{line}</p>;
              const parts = line.split(": ");
              if (parts.length >= 2) {
                return <p key={i}><span className="text-slate-400">{parts[0]}:</span> <span className="text-slate-700">{parts.slice(1).join(": ")}</span></p>;
              }
              return <p key={i}>{line}</p>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TrashedLeadRow({ lead, onRestore, onDelete }) {
  const ts = lead.trashed_at ? new Date(lead.trashed_at).toLocaleString() : "";
  return (
    <div data-testid={`trashed-lead-${lead.id}`} className="flex items-center gap-3 p-3 border border-red-100 rounded-lg bg-red-50/30">
      <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
        <Trash2 className="h-4 w-4 text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-600 truncate">{lead.name}</p>
        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
          {lead.email && <span>{lead.email}</span>}
          {ts && <span>Trashed: {ts}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button data-testid={`restore-lead-${lead.id}`} onClick={() => onRestore(lead.id)} className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
          Restore
        </button>
        <button data-testid={`delete-lead-${lead.id}`} onClick={() => onDelete(lead.id)} className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
          Delete Forever
        </button>
      </div>
    </div>
  );
}

function ContactDetail({ contact }) {
  const ts = contact.created_at ? new Date(contact.created_at).toLocaleString() : "";
  return (
    <div data-testid={`contact-row-${contact.id}`} className="border border-slate-100 rounded-lg p-3 hover:border-slate-200 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
          <MessageSquare className="h-4 w-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 truncate">{contact.name}</p>
            {contact.subject && <Badge variant="outline" className="text-[10px] shrink-0">{contact.subject}</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{contact.message}</p>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
            {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
            {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
            {ts && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ts}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditLeadDialog({ lead, open, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", insurance_type: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        insurance_type: lead.insurance_type || "",
        notes: lead.notes || "",
      });
    }
  }, [lead]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(lead.id, form);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-lead-dialog">
        <DialogHeader><DialogTitle>Edit Lead</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Name</Label>
              <Input data-testid="edit-lead-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Email</Label>
              <Input data-testid="edit-lead-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Phone</Label>
              <Input data-testid="edit-lead-phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Insurance Type</Label>
              <Input data-testid="edit-lead-type" value={form.insurance_type} onChange={e => setForm(p => ({ ...p, insurance_type: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Notes</Label>
            <Textarea data-testid="edit-lead-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="mt-1" rows={3} placeholder="Add notes about this lead..." />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline" size="sm">Cancel</Button></DialogClose>
          <Button data-testid="save-lead-btn" size="sm" onClick={handleSave} disabled={saving} className="bg-[#002147] hover:bg-[#001a3a]">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [trashedLeads, setTrashedLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("leads");
  const [loading, setLoading] = useState(true);
  const [editLead, setEditLead] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [lr, tr, cr] = await Promise.all([
        axios.get(`${API}/admin/leads?status=active`, { withCredentials: true }),
        axios.get(`${API}/admin/leads?status=trashed`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
      ]);
      setLeads(lr.data.leads || []);
      setTrashedLeads(tr.data.leads || []);
      setContacts(cr.data.contacts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEditSave = async (leadId, data) => {
    await axios.put(`${API}/admin/leads/${leadId}`, data, { withCredentials: true });
    await load();
  };

  const handleTrash = async (leadId) => {
    if (!window.confirm("Move this lead to trash?")) return;
    await axios.put(`${API}/admin/leads/${leadId}/trash`, {}, { withCredentials: true });
    await load();
  };

  const handleRestore = async (leadId) => {
    await axios.put(`${API}/admin/leads/${leadId}/restore`, {}, { withCredentials: true });
    await load();
  };

  const handlePermanentDelete = async (leadId) => {
    if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/leads/${leadId}`, { withCredentials: true });
    await load();
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/leads/export/excel`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `a2insure_leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Excel export failed", e); alert("Export failed. Please try again."); }
    finally { setExporting(false); }
  };

  const exportToGoogleSheets = () => {
    const data = leads.map(l => ({
      Name: l.name || "",
      Email: l.email || "",
      Phone: l.phone || "",
      "Insurance Type": l.insurance_type || "",
      Notes: l.notes || "",
      Message: (l.message || "").replace(/\n/g, " | "),
      "Created At": l.created_at ? new Date(l.created_at).toLocaleString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `a2insure_leads_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    // Open Google Sheets import page after CSV download
    setTimeout(() => {
      window.open("https://sheets.google.com/create", "_blank");
    }, 500);
  };

  const filteredLeads = search
    ? leads.filter(l =>
        l.name?.toLowerCase().includes(search.toLowerCase()) ||
        l.email?.toLowerCase().includes(search.toLowerCase()) ||
        l.insurance_type?.toLowerCase().includes(search.toLowerCase()) ||
        l.message?.toLowerCase().includes(search.toLowerCase())
      )
    : leads;

  const filteredContacts = search
    ? contacts.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.subject?.toLowerCase().includes(search.toLowerCase()) ||
        c.message?.toLowerCase().includes(search.toLowerCase())
      )
    : contacts;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#002147] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-leads-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("leads")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#002147]">{leads.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Chatbot Leads</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "leads" ? "bg-blue-100" : "bg-slate-100"}`}>
                <FileText className={`h-5 w-5 ${view === "leads" ? "text-blue-600" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("contacts")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#002147]">{contacts.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Contact Messages</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "contacts" ? "bg-amber-100" : "bg-slate-100"}`}>
                <MessageSquare className={`h-5 w-5 ${view === "contacts" ? "text-amber-600" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("trash")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-500">{trashedLeads.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Trash</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "trash" ? "bg-red-100" : "bg-slate-100"}`}>
                <Trash2 className={`h-5 w-5 ${view === "trash" ? "text-red-500" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#002147]">{leads.length + contacts.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Total Inquiries</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Filter className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base">
              {view === "leads" && `Chatbot Leads (${filteredLeads.length})`}
              {view === "contacts" && `Contact Messages (${filteredContacts.length})`}
              {view === "trash" && `Trash (${trashedLeads.length})`}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {view === "leads" && (
                <>
                  <Button data-testid="export-excel-btn" variant="outline" size="sm" onClick={exportToExcel} disabled={exporting || leads.length === 0} className="text-xs h-8 gap-1.5">
                    <Download className="h-3.5 w-3.5" /> {exporting ? "Exporting..." : "Excel"}
                  </Button>
                  <Button data-testid="export-gsheets-btn" variant="outline" size="sm" onClick={exportToGoogleSheets} disabled={leads.length === 0} className="text-xs h-8 gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                    <Sheet className="h-3.5 w-3.5" /> Google Sheets
                  </Button>
                </>
              )}
              {view !== "trash" && (
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="leads-search"
                    placeholder="Search name, email, type..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-8 h-8 w-56 text-xs"
                  />
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {view === "leads" && (
            filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{leads.length === 0 ? "No leads captured yet" : "No leads match your search"}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLeads.map(lead => (
                  <LeadDetail key={lead.id} lead={lead} onEdit={setEditLead} onTrash={handleTrash} />
                ))}
              </div>
            )
          )}

          {view === "contacts" && (
            filteredContacts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{contacts.length === 0 ? "No contact messages yet" : "No messages match your search"}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredContacts.map(contact => <ContactDetail key={contact.id} contact={contact} />)}
              </div>
            )
          )}

          {view === "trash" && (
            trashedLeads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Archive className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Trash is empty</p>
              </div>
            ) : (
              <div className="space-y-2">
                {trashedLeads.map(lead => (
                  <TrashedLeadRow key={lead.id} lead={lead} onRestore={handleRestore} onDelete={handlePermanentDelete} />
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Edit Lead Dialog */}
      <EditLeadDialog
        lead={editLead}
        open={!!editLead}
        onClose={() => setEditLead(null)}
        onSave={handleEditSave}
      />
    </div>
  );
}
